import React, { FC } from 'react';
import { TypographyWithElipsis } from '@components/TypographyElipsis';
import { Checkbox, Chip, MenuItem } from '@mui/material';

type Props = {
  label: string;
  checked: boolean;
  count?: number;
  onToggle: () => void;
};

/**
 * One selectable value inside a category panel.
 *
 * The whole row toggles, and the checkbox carries no handler of its own: a
 * click on it bubbles to the row, so either target fires `onToggle` exactly
 * once, and a Space press on the focused checkbox reaches the row the same
 * way. The count is omitted rather than zeroed when absent: AC5.2 hides
 * counts entirely once any value is ticked, and AC5.3 never shows one for
 * Select all, so "no pill" and "a pill reading 0" are different states the
 * caller controls by passing or withholding `count`. The checkbox, the row
 * padding and the default-size `Chip` are each already the Figma spec in the
 * theme, so nothing here is restyled.
 */
const FilterValueRow: FC<Props> = ({ label, checked, count, onToggle }) => (
  <MenuItem onClick={onToggle}>
    <Checkbox checked={checked} inputProps={{ 'aria-label': label }} />

    <TypographyWithElipsis
      variant="body1"
      color="grey.900"
      tooltipPlacement="bottom"
      sx={{ flex: 1, minWidth: 0 }}
    >
      {label}
    </TypographyWithElipsis>

    {count !== undefined && <Chip label={count} sx={{ flexShrink: 0 }} />}
  </MenuItem>
);

export default FilterValueRow;
